import type { DensityStateView } from "@screener/protocol";
import { useSyncExternalStore } from "react";
import { sameCountRecord } from "./use-stable";

/**
 * Счётчики экрана плотностей: «N из M» и числа у стадий и бирж. Они меняются
 * почти каждый кадр потока (плотности рождаются и уходят), и пропсами в
 * полосу фильтров они будили бы её целиком, вместе с фишками и сортировкой.
 * Поэтому экран кладёт их в это мини-хранилище, а числа рисуют листья,
 * подписанные на свой примитив (`screen-count-leaves.tsx`).
 */
export interface ScreenCounts {
  /** Сколько строк показано в текущем виде. */
  visible: number;
  /** Сколько их было до фильтров. */
  total: number;
  /** Живые плотности по стадиям до применения фильтров. */
  states: Partial<Record<DensityStateView, number>>;
  /** Живые плотности по рынкам бирж («<биржа>:<рынок>», §115) до применения фильтров. */
  venues: Record<string, number>;
  /**
   * Плотности, прошедшие фильтры после заморозки вида «Карточки» §77 и ещё не
   * показанные. Вне заморозки — ноль. Число меняется с потоком, поэтому живёт
   * здесь, а не пропсом полосы.
   */
  newCount: number;
}

/**
 * Период, чаще которого счётчики полосы не меняются от потока (§84 п. 2).
 * Частота показа в клиенте, а не порог домена §11, поэтому не в конфиге.
 */
export const COUNTER_UPDATE_INTERVAL_MS = 2000;

export interface ScreenCountsSetOptions {
  /** Изменение вызвано действием пользователя: показать сразу, без ожидания окна. */
  immediate?: boolean;
}

export interface ScreenCountsStore {
  get: () => ScreenCounts;
  /**
   * Кладёт новые счётчики. Подписчиков будит, только если что-то изменилось и
   * с прошлой публикации прошёл период (или задан `immediate`); иначе значение
   * откладывается до конца окна, побеждает последнее.
   */
  set: (next: ScreenCounts, options?: ScreenCountsSetOptions) => void;
  subscribe: (listener: () => void) => () => void;
  /** Снимает отложенную публикацию; зовётся при размонтировании экрана. */
  dispose: () => void;
}

export interface ScreenCountsClock {
  /** Часы в миллисекундах; по умолчанию `performance.now` — монотонные, откат системных часов окно не растягивает. */
  now?: () => number;
  intervalMs?: number;
}

/** Свой планировщик (в тестах — ручной): тип дескриптора таймера задаёт он сам. */
export interface ScreenCountsScheduler<Handle> extends ScreenCountsClock {
  schedule: (callback: () => void, delayMs: number) => Handle;
  cancel: (handle: Handle) => void;
}

export function sameScreenCounts(left: ScreenCounts, right: ScreenCounts): boolean {
  return (
    left.visible === right.visible &&
    left.total === right.total &&
    left.newCount === right.newCount &&
    sameCountRecord(left.states, right.states) &&
    sameCountRecord(left.venues, right.venues)
  );
}

export function createScreenCounts(
  initial: ScreenCounts,
  options?: ScreenCountsClock,
): ScreenCountsStore;
export function createScreenCounts<Handle>(
  initial: ScreenCounts,
  options: ScreenCountsScheduler<Handle>,
): ScreenCountsStore;
export function createScreenCounts<Handle>(
  initial: ScreenCounts,
  options: ScreenCountsClock | ScreenCountsScheduler<Handle> = {},
): ScreenCountsStore {
  if ("schedule" in options) {
    return buildScreenCounts(initial, options);
  }
  return buildScreenCounts(initial, {
    ...options,
    schedule: (callback, delayMs) => setTimeout(callback, delayMs),
    cancel: (handle) => clearTimeout(handle),
  });
}

function buildScreenCounts<Handle>(
  initial: ScreenCounts,
  options: ScreenCountsScheduler<Handle>,
): ScreenCountsStore {
  const now = options.now ?? (() => performance.now());
  const { schedule, cancel } = options;
  const intervalMs = options.intervalMs ?? COUNTER_UPDATE_INTERVAL_MS;

  let current = initial;
  // Окно отсчитывается от последней публикации; до первой его нет, и первое
  // изменение (данные после пустой загрузки) показывается сразу.
  let publishedAt = Number.NEGATIVE_INFINITY;
  let pending: ScreenCounts | null = null;
  let timer: Handle | null = null;
  const listeners = new Set<() => void>();

  const clearPending = () => {
    pending = null;
    if (timer !== null) {
      cancel(timer);
      timer = null;
    }
  };

  const publish = (next: ScreenCounts) => {
    clearPending();
    publishedAt = now();
    if (sameScreenCounts(current, next)) {
      return;
    }
    current = next;
    for (const listener of listeners) {
      listener();
    }
  };

  return {
    get: () => current,
    set: (next, setOptions) => {
      if (sameScreenCounts(current, next)) {
        // Вернулись к показанному значению: отложенное устарело.
        clearPending();
        return;
      }
      const elapsed = now() - publishedAt;
      if (setOptions?.immediate === true || elapsed >= intervalMs) {
        publish(next);
        return;
      }
      pending = next;
      if (timer === null) {
        timer = schedule(() => {
          timer = null;
          if (pending !== null) {
            publish(pending);
          }
        }, intervalMs - elapsed);
      }
    },
    subscribe: (listener) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    dispose: clearPending,
  };
}

/**
 * Число из счётчиков. Селектор отдаёт примитив, поэтому он может быть
 * inline-замыканием: лист перерисовывается, только когда его число сменилось.
 */
export function useScreenCount(
  store: ScreenCountsStore,
  select: (counts: ScreenCounts) => number,
): number {
  return useSyncExternalStore(
    store.subscribe,
    () => select(store.get()),
    () => select(store.get()),
  );
}
